import { polygonEwkt, type GeoJsonPolygon } from '@treetrack/protocol';
import { fmtDate, h, uuid } from '../dom';
import { loadProjects, type ProjectSummary } from '../data';
import { L, t } from '../i18n';
import { toHash } from '../router';
import { getSession } from '../session';
import { supabase, unwrap } from '../supabase';
import { button, card, empty, errorBox, input, labelled, loading, pct } from './shared';

export function projectsView(): HTMLElement {
  const root = h('div', { class: 'projects' });
  const list = h('div', { class: 'cards' }, loading());
  root.appendChild(h('h1', null, t('nav.projects')));
  root.appendChild(list);
  if (getSession().isAdmin) root.appendChild(newProjectForm(() => refresh()));

  async function refresh() {
    try {
      const rows = await loadProjects();
      list.replaceChildren(...(rows.length ? rows.map(projectCard) : [empty(getSession().memberships.length || getSession().isAdmin ? t('project.none') : t('auth.no_projects'))]));
    } catch (err) {
      list.replaceChildren(errorBox(err));
    }
  }
  void refresh();
  return root;
}

function projectCard(p: ProjectSummary): HTMLElement {
  return h(
    'a',
    { class: 'card project-card', href: toHash({ name: 'project', id: p.project_id }) },
    h('div', { class: 'project-code' }, p.code),
    h('h2', null, p.name),
    h('div', { class: 'project-meta' }, t('project.trees', { n: p.tree_count }), ' · ', p.last_survey_date ? t('project.last_survey', { date: fmtDate(p.last_survey_date) }) : t('project.no_survey')),
    h('div', { class: 'project-survival' }, p.survival_pct != null ? t('project.survival', { pct: pct(p.survival_pct).replace('%', '') }) : t('project.no_survey')),
  );
}

function newProjectForm(onDone: () => void): HTMLElement {
  const code = input({ placeholder: 'DS', pattern: '[A-Z]{2,4}', maxlength: 4, required: true, style: { textTransform: 'uppercase' } });
  const name = input({ placeholder: 'San Juan del Sur Day School', required: true });
  const site = input({ placeholder: 'Site / address' });
  const planted = input({ type: 'date' });
  const boundary = h('textarea', { class: 'input', rows: 3, placeholder: '{"type":"Polygon","coordinates":[[[lng,lat],...]]}' });
  const status = h('div');
  const form = h(
    'form',
    {
      class: 'form',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        status.replaceChildren();
        try {
          let boundaryEwkt: string | null = null;
          if (boundary.value.trim()) {
            const poly = JSON.parse(boundary.value) as GeoJsonPolygon;
            if (poly.type !== 'Polygon') throw new Error('boundary must be a GeoJSON Polygon');
            boundaryEwkt = polygonEwkt(poly);
          }
          unwrap(
            await supabase!.from('projects').insert({
              id: uuid(),
              code: code.value.trim().toUpperCase(),
              name: name.value.trim(),
              site: site.value.trim() || null,
              planted_on: planted.value || null,
              boundary: boundaryEwkt,
            }),
          );
          form.reset();
          onDone();
        } catch (err) {
          status.replaceChildren(errorBox(err));
        }
      },
    },
    labelled(t('auth.project_code'), code),
    labelled(L('Nombre', 'Name'), name),
    labelled(L('Sitio', 'Site'), site),
    labelled(t('field.planted_on'), planted),
    labelled(L('Límite (GeoJSON Polygon, opcional)', 'Boundary (GeoJSON Polygon, optional)'), boundary),
    h('button', { type: 'submit', class: 'btn btn-primary' }, t('admin.new_project')),
    status,
  );
  const details = h('details', { class: 'card' }, h('summary', null, t('admin.new_project')), form);
  return details;
}

export { button };
